// Helper for parsing, cleaning and translating API errors into user-friendly messages

export const FIELD_LABELS = {
  executor_id: 'Производитель работ',
  executor: 'Производитель работ',
  manager_id: 'Руководитель работ',
  manager: 'Руководитель работ',
  approving_id: 'Допускающий',
  approving: 'Допускающий',
  observer_id: 'Наблюдающий',
  observer: 'Наблюдающий',
  task_type_id: 'Вид работ',
  task_type: 'Вид работ',
  branch_id: 'Филиал',
  branch: 'Филиал',
  start_date: 'Дата начала',
  end_date: 'Дата окончания',
  start_datetime: 'Дата и время начала',
  finish_datetime: 'Дата и время окончания',
  brigade_members: 'Члены бригады',
  brigade: 'Члены бригады',
  entrusted: 'Поручается',
  description: 'Описание',
  cancel_description: 'Причина отмены',
  visiting_plase_id: 'Место прибытия',
  visitingplace_id: 'Место прибытия',
  driver_id: 'Водитель',
  driver: 'Водитель',
  iin: 'ИИН',
  phone: 'Номер телефона',
  username: 'Логин',
  password: 'Пароль',
  name: 'Наименование',
  status: 'Статус',
  otherTaskTypeName: 'Вид работ другое',
  otherVisitingName: 'Место прибытия другое',
  time_zone: 'Часовой пояс',
};

const COMMON_TRANSLATIONS = [
  { pattern: /User not found/gi, replacement: 'Сотрудник не найден в системе' },
  { pattern: /Branch not found/gi, replacement: 'Филиал не найден в системе' },
  { pattern: /Task Type not found/gi, replacement: 'Вид работ не найден в системе' },
  { pattern: /This field is required\.?/gi, replacement: 'Обязательно для заполнения' },
  { pattern: /This field may not be null\.?/gi, replacement: 'Не может быть пустым' },
  { pattern: /This field may not be blank\.?/gi, replacement: 'Не может быть пустым' },
  { pattern: /A valid integer is required\.?/gi, replacement: 'Требуется указать числовое значение' },
  { pattern: /Invalid pk.*?does not exist/gi, replacement: 'Выбранная запись не найдена' },
  { pattern: /Invalid username or password/gi, replacement: 'Неверный логин или пароль' },
  { pattern: /Authentication credentials were not provided\.?/gi, replacement: 'Требуется авторизация в системе' },
  { pattern: /You do not have permission to perform this action\.?/gi, replacement: 'У вас нет прав для выполнения этого действия' },
  { pattern: /Method.*?not allowed/gi, replacement: 'Действие не поддерживается сервером' },
  { pattern: /Not found\.?/gi, replacement: 'Запрошенные данные не найдены' },
  { pattern: /validation_error/gi, replacement: 'Ошибка валидации данных' },
  { pattern: /internal_server_error/gi, replacement: 'Внутренняя ошибка сервера' },
  { pattern: /Network Error/gi, replacement: 'Ошибка сети. Проверьте подключение к серверу' },
  { pattern: /timeout of \d+ms exceeded/gi, replacement: 'Время ожидания ответа от сервера истекло' },
];

/**
 * Очищает строку от JSON-мусора и переводит системные фразы
 */
export function cleanErrorMessage(text) {
  if (!text) return '';
  let str = String(text).trim();

  // Удаляем префикс 'error: ' если есть
  if (/^error:\s*/i.test(str)) {
    str = str.replace(/^error:\s*/i, '');
  }

  // Если это JSON-строка, пытаемся распарсить
  if ((str.startsWith('{') && str.endsWith('}')) || (str.startsWith('[') && str.endsWith(']'))) {
    try {
      const parsed = JSON.parse(str);
      const extracted = extractFromData(parsed);
      if (extracted.length > 0) {
        return extracted.join('; ');
      }
    } catch (e) {
      // не JSON, продолжаем обработку как строки
    }
  }

  // Применяем словарь системных замен
  for (const item of COMMON_TRANSLATIONS) {
    str = str.replace(item.pattern, item.replacement);
  }

  // Заменяем технические названия полей с двоеточием, например "executor_id: ..." -> "Производитель работ: ..."
  for (const [key, label] of Object.entries(FIELD_LABELS)) {
    const fieldRegex = new RegExp(`(^|\\s)${key}:`, 'g');
    str = str.replace(fieldRegex, `$1${label}:`);
  }

  return str;
}

/**
 * Извлекает человекопонятные сообщения из любых структур данных (массивов, объектов, DRF response)
 */
function extractFromData(data) {
  const messages = [];

  if (!data) return messages;

  if (typeof data === 'string') {
    const cleaned = cleanErrorMessage(data);
    if (cleaned) messages.push(cleaned);
    return messages;
  }

  if (Array.isArray(data)) {
    data.forEach((item) => {
      if (typeof item === 'string') {
        const cleaned = cleanErrorMessage(item);
        if (cleaned) messages.push(cleaned);
      } else if (item && typeof item === 'object') {
        messages.push(...extractFromData(item));
      }
    });
    return messages;
  }

  if (typeof data === 'object') {
    // 1. Формат стандарта backend: { success: false, error: { code, message, details } }
    if (data.error && typeof data.error === 'object') {
      const errObj = data.error;
      if (errObj.details) {
        const detailMsgs = extractFromData(errObj.details);
        if (detailMsgs.length > 0) {
          return detailMsgs;
        }
      }
      if (errObj.message) {
        return [cleanErrorMessage(errObj.message)];
      }
    }

    // 2. Стандартный Django REST detail: { detail: '...' }
    if (data.detail) {
      if (typeof data.detail === 'string') {
        return [cleanErrorMessage(data.detail)];
      }
      return extractFromData(data.detail);
    }

    // 3. Сообщение первого уровня: { message: '...' }
    if (data.message && typeof data.message === 'string') {
      return [cleanErrorMessage(data.message)];
    }

    // 4. Словарь ошибок полей { field_name: ['Error 1', ...] }
    Object.entries(data).forEach(([key, val]) => {
      // Игнорируем служебные ключи
      if (key === 'code' || key === 'status' || key === 'success') {
        return;
      }
      const fieldLabel = FIELD_LABELS[key] || key;
      if (Array.isArray(val)) {
        const valText = val.map((v) => cleanErrorMessage(v)).join(', ');
        messages.push(`${fieldLabel}: ${valText}`);
      } else if (typeof val === 'string') {
        messages.push(`${fieldLabel}: ${cleanErrorMessage(val)}`);
      } else if (val && typeof val === 'object') {
        const subMsgs = extractFromData(val);
        if (subMsgs.length > 0) {
          messages.push(`${fieldLabel}: ${subMsgs.join(', ')}`);
        }
      }
    });
  }

  return messages;
}

/**
 * Главная функция форматирования ошибок API.
 * Принимает error (Axios error, объект ошибки, массив или строку).
 * Всегда возвращает массив понятных русских строк.
 */
export function formatApiErrors(error) {
  if (!error) {
    return ['Произошла ошибка при выполнении запроса'];
  }

  // Если это Axios error с ответом сервера
  const responseData = error?.response?.data;
  if (responseData) {
    const extracted = extractFromData(responseData);
    if (extracted.length > 0) {
      // Удаляем дубликаты
      return Array.from(new Set(extracted));
    }
  }

  // Ошибка сети или таймаут
  if (error?.message) {
    const cleaned = cleanErrorMessage(error.message);
    if (cleaned) {
      return [cleaned];
    }
  }

  // Если передан просто текст или объект
  const fallback = extractFromData(error);
  if (fallback.length > 0) {
    return Array.from(new Set(fallback));
  }

  return ['Произошла ошибка при выполнении операции'];
}
